import { defineMetrics } from "../types.js"

/**
 * ⚠ **Login events are identified by `log.type`, not `log.action`.**
 */
const SESSION_CAVEAT =
  "logout is only written on an explicit sign-out (lib/Auth/Signout.js), so a session that times out has " +
  "no closing event. Following the dictionary's own fallback, an unclosed session ends at the user's last " +
  "recorded module activity; failing that it is capped at 12 hours, and it can never run past that user's " +
  "next login. All three endings are estimates, which is why the value is marked approximate (roadmap R-07)."

/** activity — platform activity (7 metrics, all new, 5 Must) */
export const activityMetrics = defineMetrics([
  {
    id: "login_frequency",
    name: "Login Frequency",
    description: "Number of times a user logged into the platform during the selected reporting period.",
    logic: "Count all successful logins within the selected date range.",
    source: "log(type='login') counted per period × user — note: type, not action",
    domain: "activity",
    srvModule: "activity",
    priority: "must",
    valueType: "count",
    dims: ["user"],
    rollup: "sum",
    defaultWidget: "bar",
    altWidgets: ["line", "hbar", "table"],
    coveredByV1: false,
    xlsxRow: 2,
  },
  {
    id: "total_time_spent_platform",
    name: "Total Time Spent on Platform",
    description: "Total amount of time a user spent actively using the platform.",
    logic:
      "For each session, duration = logout_timestamp − login_timestamp (or last-recorded-activity timestamp if the session times out without an explicit logout); Total Time Spent = Σ session durations for sessions overlapping the selected period, expressed as hours + minutes (e.g. “4h 32m”).",
    domain: "activity",
    srvModule: "activity",
    priority: "must",
    valueType: "duration_sec",
    dims: ["user"],
    rollup: "sum",
    defaultWidget: "stat-tile",
    altWidgets: ["line", "bar", "table"],
    coveredByV1: false,
    approximate: true,
    caveat: SESSION_CAVEAT,
    xlsxRow: 3,
  },
  {
    id: "dau",
    name: "Daily Active Users (DAU)",
    description: "Number of distinct users active each day.",
    logic: "Count distinct users performing meaningful activity each day.",
    source: "log(type='login'), distinct users per day / week / month",
    domain: "activity",
    srvModule: "activity",
    priority: "must",
    valueType: "count",
    dims: [],
    rollup: "avg",
    defaultWidget: "line",
    altWidgets: ["bar", "stat-tile"],
    coveredByV1: false,
    xlsxRow: 80,
  },
  {
    id: "wau",
    name: "Weekly Active Users (WAU)",
    description: "Number of distinct users active each week.",
    logic: "Count distinct users with activity during a calendar week.",
    domain: "activity",
    srvModule: "activity",
    priority: "must",
    valueType: "count",
    dims: [],
    rollup: "avg",
    defaultWidget: "line",
    altWidgets: ["bar", "stat-tile"],
    coveredByV1: false,
    xlsxRow: 81,
  },
  {
    id: "mau",
    name: "Monthly Active Users (MAU)",
    description: "Number of distinct users active each month.",
    logic: "Count distinct users with activity during a calendar month.",
    domain: "activity",
    srvModule: "activity",
    priority: "must",
    valueType: "count",
    dims: [],
    rollup: "avg",
    defaultWidget: "line",
    altWidgets: ["bar", "stat-tile"],
    coveredByV1: false,
    xlsxRow: 82,
  },
  {
    id: "avg_session_duration",
    name: "Average Session Duration",
    description: "Average duration of user session (one session is from login to logout).",
    logic: "Total session duration ÷ Total number of sessions.",
    domain: "activity",
    srvModule: "activity",
    priority: "nice",
    valueType: "duration_sec",
    dims: [],
    rollup: "avg",
    defaultWidget: "stat-tile",
    altWidgets: ["line"],
    coveredByV1: false,
    approximate: true,
    caveat: SESSION_CAVEAT,
    xlsxRow: 85,
  },
  {
    id: "activity_trends",
    name: "Activity Trends Over Time",
    description: "Changes in user activity over time.",
    logic: "Plot activity counts by day, week, or month.",
    source: "log(login|logout) + auditTrail, merged event count per period",
    domain: "activity",
    srvModule: "activity",
    priority: "nice",
    valueType: "count",
    dims: [],
    rollup: "sum",
    defaultWidget: "line",
    altWidgets: ["bar"],
    coveredByV1: false,
    xlsxRow: 86,
  },
])
